import React from "react";

interface ProficiencyIndicatorProps {
  proficiency: number;
  max: number;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
}

const sizeMap = {
  sm: "w-3 h-3",
  md: "w-4 h-4",
  lg: "w-5 h-5",
};

const getColor = (index: number, max: number): string => {
  const ratio = (index + 1) / max;
  if (ratio <= 0.33) return "bg-red-500";
  if (ratio <= 0.66) return "bg-yellow-500";
  return "bg-green-500";
};

const ProficiencyIndicator: React.FC<ProficiencyIndicatorProps> = ({
  proficiency,
  max,
  size = "md",
  showLabel = false,
}) => {
  const dotSize = sizeMap[size];

  return (
    <div className="flex items-center gap-1.5">
      {/*
        The level is conveyed purely by how many dots are coloured in, which a
        screen reader cannot see at all — and the numeric label is optional and
        off by default. role="meter" exposes the actual value.
      */}
      <div
        className="flex items-center gap-1"
        role="meter"
        aria-valuenow={proficiency}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={`Proficiency ${proficiency} of ${max}`}
      >
        {Array.from({ length: max }, (_, i) => (
          <div
            key={i}
            data-testid="proficiency-dot"
            className={`${dotSize} rounded-full transition-colors ${
              i < proficiency
                ? getColor(i, max)
                : "bg-gray-300 dark:bg-gray-600"
            }`}
          />
        ))}
      </div>
      {showLabel && (
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
          {proficiency}/{max}
        </span>
      )}
    </div>
  );
};

export default ProficiencyIndicator;
